import React from 'react';
import Button from '../ui/Button';
import { usePortfolio } from '../../contexts/PortfolioContext'
import { PersonalInfo } from '../../types/entityTypes';

const Hero: React.FC = () => {
  const { data, loading, error } = usePortfolio();

  if (loading) return <div></div>;
  if (error) return <div>Error: {error}</div>;

  const personalInfo: PersonalInfo = data?.personalInfo;

  return (
    <section id="home" className="hero-section min-h-screen flex items-center pt-16 transition-colors duration-300">
      <div className="hero-grid" aria-hidden="true" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left — Text Content */}
          <div className="hero-copy animate-slide-up">
            <div className="availability-pill"><span /> Building at the intersection of AI, systems and human progress</div>
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 dark:text-white mb-6 transition-colors duration-300">
              <span className="hero-kicker block text-lg md:text-xl font-medium text-gray-500 dark:text-gray-400 mb-2">Hello, I’m</span>
              <span className="hero-name block text-primary-600 dark:text-primary-400 font-display">
                {personalInfo.name}
              </span>
            </h1>

            <h2 className="hero-title text-xl md:text-2xl text-gray-600 dark:text-gray-300 mb-6 font-light transition-colors duration-300">
              I turn meaningful problems into intelligent products.
            </h2>

            <p className="text-base text-gray-700 dark:text-gray-300 mb-8 max-w-xl leading-relaxed transition-colors duration-300">
              I’m a builder, engineer and endlessly curious problem solver. I explore how AI, thoughtful product design and dependable systems can make complex things simpler—and useful ideas real.
            </p>

            {/* Call to Action Buttons */}
            <div className="hero-actions">
              <div className="hero-action-buttons">
                <Button
                  variant="primary"
                  size="md"
                  className="hero-action-primary"
                  onClick={() => document.getElementById('projects')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  Explore what I’m building <span aria-hidden="true">↓</span>
                </Button>

                <Button
                  variant="outline"
                  size="md"
                  className="hero-action-secondary"
                  onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  Start a conversation <span aria-hidden="true">↗</span>
                </Button>
              </div>

              {personalInfo.resumeUrl && (
                <a className="hero-resume-link" href={personalInfo.resumeUrl} target="_blank" rel="noopener noreferrer">
                  <span className="hero-resume-icon" aria-hidden="true">PDF</span>
                  <span><strong>Prefer the traditional version?</strong> View my formal résumé</span>
                  <i aria-hidden="true">↗</i>
                </a>
              )}
            </div>

            {/* Social Links */}
            <div className="mt-10">
              <div className="flex space-x-5">
                <a
                  href={personalInfo.github}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors duration-200"
                  aria-label="GitHub"
                >
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
                  </svg>
                </a>

                <a
                  href={personalInfo.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors duration-200"
                  aria-label="LinkedIn"
                >
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                  </svg>
                </a>

                <a
                  href={`mailto:${personalInfo.email}`}
                  className="text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors duration-200"
                  aria-label="Email"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="4" width="20" height="16" rx="2" />
                    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                  </svg>
                </a>
              </div>
            </div>
          </div>

          {/* Right — 3D Avatar */}
          <div className="flex justify-center lg:justify-end animate-fade-in -mt-4 lg:-mt-8">
            <img
              src="/sachin-avatar.png"
              alt="Sachin Saini 3D Avatar"
              className="hero-avatar w-full max-w-[36rem] object-contain drop-shadow-2xl animate-float"
            />
          </div>
        </div>

        {/* Scroll Down Indicator */}
        <div className="mt-16 text-center animate-bounce-slow">
          <button
            onClick={() => document.getElementById('about')?.scrollIntoView({ behavior: 'smooth' })}
            className="text-gray-400 dark:text-gray-500 hover:text-primary-600 dark:hover:text-primary-400 transition-colors duration-200"
            aria-label="Scroll down"
          >
            <svg className="w-6 h-6 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
          </button>
        </div>
      </div>
    </section>
  );
};

export default Hero;
